// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useState } from 'react';
import { Modal, RadioButton, RadioButtonGroup } from '@carbon/react';
import type { GatePayload } from '../../../services/onboardingService';
import type { ChecklistItem, DocumentLink } from '../../../types';

interface ReviewMatchModalProps {
  /** Live gate question from the workflow stream, when one is pending. */
  gate: GatePayload | null;
  /** Checklist item under review when opened from a table row. */
  item: ChecklistItem | null;
  documents: DocumentLink[];
  onAnswerGate: (_gate: GatePayload, _selectedOptionId: string, _docType: string) => void;
  /** Hand the item to the bypass flow (weak matches are resolved by either a
   * stronger future classification or a bypass — this is the bypass path). */
  onBypass: (_item: ChecklistItem) => void;
  onClose: () => void;
}

/**
 * "Review match" dialog. Two modes:
 * - A live gate question (classification ambiguity): present the candidate
 *   options and resume the workflow with the selection.
 * - No live gate: match details for a needs-review row (weak match, e.g.
 *   matched by type only) with "Bypass with a reason" as the primary action —
 *   the only decision available for a weak match, and without it a required
 *   needs-review row would block submit with no way out.
 */
export default function ReviewMatchModal({
  gate,
  item,
  documents,
  onAnswerGate,
  onBypass,
  onClose,
}: ReviewMatchModalProps) {
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const open = gate !== null || item !== null;

  const handleClose = () => {
    setSelectedOptionId(null);
    onClose();
  };

  if (gate) {
    const selected = gate.candidate_options.find(option => option.option_id === selectedOptionId);
    return (
      <Modal
        open={open}
        modalHeading={`Review classification: ${gate.filename}`}
        primaryButtonText="Confirm selection"
        secondaryButtonText="Cancel"
        primaryButtonDisabled={!selected}
        onRequestClose={handleClose}
        onRequestSubmit={() => {
          if (selected) {
            onAnswerGate(gate, selected.option_id, selected.doc_type);
            setSelectedOptionId(null);
          }
        }}
      >
        <p className="obw-modal-note">{gate.question_prompt}</p>
        <RadioButtonGroup
          name="review-match-options"
          orientation="vertical"
          legendText="Candidate classifications"
          valueSelected={selectedOptionId ?? undefined}
          onChange={(value: string | number | undefined) =>
            setSelectedOptionId(value != null ? String(value) : null)
          }
        >
          {gate.candidate_options.map(option => (
            <RadioButton
              key={option.option_id}
              value={option.option_id}
              labelText={`${option.doc_type} (${Math.round(option.confidence_score * 100)}%) — ${option.rationale}`}
            />
          ))}
        </RadioButtonGroup>
      </Modal>
    );
  }

  if (item) {
    const matchedDocs = item.satisfied_by
      .map(id => documents.find(doc => doc.document_id === id))
      .filter(Boolean) as DocumentLink[];
    return (
      <Modal
        open={open}
        modalHeading={`Match details: ${item.expected_doc_type}`}
        primaryButtonText="Bypass with a reason"
        secondaryButtonText="Not now"
        onRequestClose={handleClose}
        onRequestSubmit={() => {
          handleClose();
          onBypass(item);
        }}
      >
        {matchedDocs.length > 0 ? (
          <ul className="obw-match-details">
            {matchedDocs.map(doc => (
              <li key={doc.document_id}>
                {doc.filename ?? doc.document_id}
                {typeof doc.confidence === 'number'
                  ? ` · ${Math.round(doc.confidence * 100)}% confidence`
                  : ''}
                {doc.doc_type ? ` · classified as ${doc.doc_type}` : ''}
              </li>
            ))}
          </ul>
        ) : (
          <p className="obw-modal-note">
            Matched by type only — no strong document match yet. A closer match arrives with the
            next classification run, or bypass this item now with a reason; it reopens automatically
            if a matching document arrives later.
          </p>
        )}
      </Modal>
    );
  }

  return null;
}
